/**
 * Wallet redesign (Sept 2026) — the pure logic behind the transaction list. Nothing here
 * touches the DOM: the page and the tests both read it.
 * Handoff: ~/Downloads/design_handoff_settings_wallet_friends/README.md §2.
 */

/** "+$5.00" / "−$2.10" (a real minus sign, never red). */
export const signedMoney = (cents) => `${cents < 0 ? '−' : '+'}$${(Math.abs(cents) / 100).toFixed(2)}`;
export const money = (cents) => `$${(Math.abs(cents || 0) / 100).toFixed(2)}`;

/** Types whose credit sits in escrow until the event (or gig) ends. */
const HELD_TYPES = new Set(['HostEventEarning', 'CoHostEventEarning', 'GuestCheckoutEarning', 'ProGigEarning', 'GatheringEscrowCredit']);
const HELD_RE = /\s*\((held until[^)]*)\)/i;
const BOOKING_RE = /\s*\[booking:[^\]]*\]/i;
const PCOIN_RE = /\bP\$/g;

const RELEASE_TYPES = new Set(['HostEscrowRelease', 'CoHostEscrowRelease', 'ProGigEscrowRelease', 'DisputeRelease', 'GatheringEscrowRelease', 'GatheringRefundReservationRelease', 'GatheringWalletReservationRelease']);
const EARNING_TYPES = new Set(['ProGigEarning', 'CoHostEventEarning', 'HostFlakeFeeEarning', 'Bonus', 'PartyPatronGiftGuest', 'PartyPatronGiftHost', 'EventSponsorGiftGuest', 'EventSponsorPayoutHost', 'PeerTransferReceived']);
const REFUND_TYPES = new Set(['CommitmentRefund', 'RefundToBalance', 'RefundToCardDebit', 'ClubMembershipRefund', 'ClubMembershipRefundDebit', 'ProBookingRefund', 'GatheringCardRefund', 'GatheringRefundReservation']);
const TICKET_TYPES = new Set(['HostEventEarning', 'GuestCheckoutEarning', 'Commitment', 'CardCharge', 'Upgrade', 'GatheringEscrowCredit', 'GatheringWalletReservation']);

export const GLYPHS = {
  release: { glyph: '↥', bg: '#DCFCE7', fg: '#15803D' },
  earning: { glyph: '★', bg: '#E0E7FF', fg: '#000' },
  club: { glyph: '♣', bg: '#FFCCE6', fg: '#000' },
  refund: { glyph: '↺', bg: '#F2F2F2', fg: '#000' },
  ticket: { glyph: '▣', bg: '#E0F2FE', fg: '#000' },
  topup: { glyph: '+', bg: '#000', fg: '#fff' },
  withdrawal: { glyph: '↧', bg: '#F2F2F2', fg: '#000' },
  sent: { glyph: '↗', bg: '#F2F2F2', fg: '#000' },
  other: { glyph: '·', bg: '#F2F2F2', fg: '#000' },
};

/** The enum name, whatever shape the API sent it in (string or numeric fallback). */
export const typeKey = (tx) => (typeof tx.type === 'string' ? tx.type : String(tx.typeDisplay || '').replace(/\s+/g, ''));

/** "ProGigEscrowRelease" → "PRO GIG ESCROW RELEASE"; "Co-Host Earning" → "CO-HOST EARNING". */
export function readableType(tx) {
  const t = typeKey(tx);
  if (t === 'HostEventEarning' && tx.amount < 0) return 'EVENT EARNING REVERSED';
  const display = tx.typeDisplay && /\s|-/.test(tx.typeDisplay) ? tx.typeDisplay : (tx.typeDisplay || t);
  return display
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toUpperCase()
    .trim();
}

/** Is the money still in escrow? The HELD pill and the REF line say so instead of the description. */
export function isHeld(tx) {
  if (tx.amount <= 0) return false;
  if (HELD_RE.test(tx.description || '')) return true;
  return HELD_TYPES.has(typeKey(tx));
}

/** Drop "(held until event completes)" and "[booking:…]"; P$ is retired. */
export function cleanDescription(tx) {
  const raw = tx.description || tx.gatheringName || tx.eventTitle || '';
  return raw.replace(HELD_RE, '').replace(BOOKING_RE, '').replace(PCOIN_RE, '$').replace(/\s{2,}/g, ' ').trim();
}

/**
 * Where the row's receipt lives: a gathering's entry row opens the gathering, an event row the
 * event. A gathering entry is never an event, so it is checked first.
 */
export function receiptPath(tx) {
  if (tx.gatheringId) return `/gatherings/${tx.gatheringId}`;
  if (tx.eventId) return `/events/${tx.eventId}`;
  return null;
}

export function glyphFor(tx) {
  const t = typeKey(tx);
  if (t === 'TopUp') return GLYPHS.topup;
  if (t === 'Withdrawal') return GLYPHS.withdrawal;
  if (t === 'PeerTransferSent') return GLYPHS.sent;
  if (RELEASE_TYPES.has(t)) return GLYPHS.release;
  if (t.startsWith('ClubMembership') && !t.includes('Refund')) return GLYPHS.club;
  if (REFUND_TYPES.has(t) || (t === 'HostEventEarning' && tx.amount < 0)) return GLYPHS.refund;
  if (EARNING_TYPES.has(t)) return GLYPHS.earning;
  if (TICKET_TYPES.has(t)) return GLYPHS.ticket;
  return GLYPHS.other;
}

/** "TX-4A9F2C1B" from the row id. */
export const refOf = (tx) => `TX-${String(tx.id || '').replace(/-/g, '').slice(0, 8).toUpperCase() || '—'}`;

const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** TODAY · YESTERDAY · THIS WEEK · EARLIER IN SEPTEMBER · AUGUST · AUGUST 2025. */
export function dayGroup(iso, now = new Date()) {
  const d = new Date(iso);
  const today = startOfDay(now);
  if (d >= today) return 'TODAY';
  if (d >= new Date(today.getTime() - 86400000)) return 'YESTERDAY';
  if (d >= new Date(today.getTime() - 6 * 86400000)) return 'THIS WEEK';
  const month = MONTHS[d.getMonth()];
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) return `EARLIER IN ${month}`;
  return d.getFullYear() === now.getFullYear() ? month : `${month} ${d.getFullYear()}`;
}

/** "TODAY · 9:14 PM" · "YESTERDAY" · "SEP 19" · "AUG 2, 2025". */
export function whenLabel(iso, now = new Date()) {
  const d = new Date(iso);
  const g = dayGroup(iso, now);
  if (g === 'TODAY') return `TODAY · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase()}`;
  if (g === 'YESTERDAY') return 'YESTERDAY';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined }).toUpperCase();
}

/** "SEP 19, 2026 · 6:02 PM" for the detail strip. */
export function fullDate(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`.toUpperCase();
}

export const FILTERS = ['all', 'in', 'out', 'held'];

export function matchesFilter(tx, f) {
  if (f === 'in') return tx.amount > 0;
  if (f === 'out') return tx.amount < 0;
  if (f === 'held') return isHeld(tx);
  return true;
}

export function filterCounts(list) {
  return {
    all: list.length,
    in: list.filter((t) => t.amount > 0).length,
    out: list.filter((t) => t.amount < 0).length,
    held: list.filter(isHeld).length,
  };
}

/** Every row decorated for the page. */
export function decorate(tx, now = new Date()) {
  return {
    ...tx,
    label: readableType(tx),
    desc: cleanDescription(tx),
    held: isHeld(tx),
    glyph: glyphFor(tx),
    when: whenLabel(tx.createdAt, now),
    date: fullDate(tx.createdAt),
    ref: refOf(tx),
    group: dayGroup(tx.createdAt, now),
  };
}

/** [{ label, items, net }] in list order (the API sends newest first), empty groups dropped. */
export function groupTransactions(rows, { group = true } = {}) {
  if (!group) return [{ label: '', items: rows, net: rows.reduce((s, r) => s + r.amount, 0), showHead: false }];
  const out = [];
  const byLabel = new Map();
  for (const r of rows) {
    let g = byLabel.get(r.group);
    if (!g) { g = { label: r.group, items: [], net: 0, showHead: true }; byLabel.set(r.group, g); out.push(g); }
    g.items.push(r);
    g.net += r.amount;
  }
  return out;
}
